import { deviceApi, HttpClient } from '@/common';
import { BackendService } from '@/types/BackendService';
import { Device as DeviceData } from '@argyrelabs/iagro-types';
import { create } from 'zustand';
import useUserStore from './useUserStore';

type DeviceStore = {
	devices: DeviceData;
	allDevices: DeviceData[];
	adminDevices: DeviceData[];
	deviceWifiSignals: { [key: string]: string };
	allWifiSignals: { [key: string]: string };
	getAllDevices: (options?: { inyectWifiSignal?: boolean }) => Promise<void>;
	getAllDevicesAdmin: () => Promise<void>;
	getDeviceWifiSignal: (company: string, device: string) => Promise<void>;
	getCompanyDevicesWifiSignals: (company: string, devices: string[]) => Promise<void>;
	getAllWifiSignals: () => Promise<void>;
	loading: boolean;
	initialWifiLoading: boolean;
	setDevices: (devices: DeviceData) => void;
	setLoading: (loading: boolean) => void;
	getInitialDevice: () => Promise<void>;
	inyectWifiSignal: () => Promise<void>;
	createDevice: (deviceData: Partial<DeviceData>) => Promise<any>;
	updateDevice: (deviceId: string, deviceData: Partial<DeviceData>) => Promise<any>;
};

const useDevicesStore = create<DeviceStore>((set) => ({
	devices: {
		uid: '',
		deviceName: '',
		location: '',
		blynkId: '',
		companyId: '',
		sensors: '',
		createdAt: '',
		updatedAt: '',
	},
	setDevices: (devices: Partial<DeviceData>) => {
		set((prevState) => ({
			devices: {
				...prevState.devices,
				...devices,
			},
		}));
	},

	deviceWifiSignals: {},

	//Endpoint para obtener wifi individual
	getDeviceWifiSignal: async (company: string, device: string) => {
		try {
			const { data: response } = await HttpClient(BackendService.Realtime).get(
				'/sensors/wifi-signal',
				{
					params: {
						company,
						device,
					},
				}
			);

			if (response && response.value) {
				set((state) => ({
					deviceWifiSignals: {
						...state.deviceWifiSignals,
						[device]: response.value,
					},
				}));
			}
		} catch (error) {
			console.error('Error fetching wifi signal:', error);
		}
	},

	//Endpoint para obtener wifi grupal (por compañía)
	getCompanyDevicesWifiSignals: async (company: string, devices: string[]) => {
		try {
			const { data: response } = await HttpClient(BackendService.Realtime).get(
				'/sensors/wifi-signals',
				{
					params: {
						company,
						devices: devices.join(','),
					},
				}
			);

			if (response?.devices) {
				const newSignals: { [key: string]: string } = {};
				response.devices.forEach((device: { deviceId: string; value: string }) => {
					newSignals[device.deviceId] = device.value;
				});
				set({ deviceWifiSignals: newSignals });
			}
		} catch (error) {
			console.error('Error fetching all wifi signals:', error);
		}
	},

	allWifiSignals: {},
	initialWifiLoading: true,
	getAllWifiSignals: async () => {
		try {
			const { data: response } = await HttpClient(BackendService.Realtime).get(
				'/sensors/all-wifi-signals'
			);

			if (response?.devices) {
				const newSignals: { [key: string]: string } = {};
				response.devices.forEach((device: { deviceId: string; value: string }) => {
					newSignals[device.deviceId] = device.value;
				});
				set({ allWifiSignals: newSignals, initialWifiLoading: false });
			}
		} catch (error) {
			console.error('Error fetching system wifi signals:', error);
			set({ initialWifiLoading: false });
		}
	},

	adminDevices: [],
	getAllDevicesAdmin: async () => {
		set({ loading: true });
		try {
			const response = await deviceApi.getAllDevicesAdmin();
			const parsedDevices = parseDeviceData(response.data);
			set({ adminDevices: parsedDevices });
		} catch (error) {
			console.error('Error fetching system devices:', error);
		} finally {
			set({ loading: false });
		}
	},

	allDevices: [],
	getAllDevices: async (options?: { inyectWifiSignal?: boolean }) => {
		set({ loading: true });
		const userProfile = useUserStore.getState().userProfile;
		try {
			const res = await deviceApi.getAllDevice(userProfile.company.uid);
			const resParsedDevices = parseDeviceData(res.data);

			set({ allDevices: resParsedDevices });

			if (options?.inyectWifiSignal) {
				useDevicesStore.getState().inyectWifiSignal();
			}

			// Set first device as current device
			set({ devices: res.data[0] });
		} catch (error) {
			console.error('Error fetching devices:', error);
		} finally {
			set({ loading: false });
		}
	},

	getInitialDevice: async () => {
		await getInitialDevice();
	},

	loading: true,
	setLoading: (loading: boolean) => {
		set((prevState) => ({
			...prevState,
			loading,
		}));
	},

	inyectWifiSignal: async () => {
		const devices = useDevicesStore.getState().allDevices;
		const userProfile = useUserStore.getState().userProfile;

		const deviceIds = devices.map((device) => device.uid);
		useDevicesStore.getState().getCompanyDevicesWifiSignals(userProfile.company.uid, deviceIds);
	},

	createDevice: async (deviceData) => {
		set({ loading: true });
		try {
			const response = await deviceApi.createDevice(deviceData);

			await useDevicesStore.getState().getAllDevicesAdmin();

			return response.data;
		} catch (error) {
			console.error('Error detallado al crear dispositivo:', error);
			throw error;
		} finally {
			set({ loading: false });
		}
	},

	updateDevice: async (deviceId, deviceData) => {
		set({ loading: true });
		try {
			const response = await deviceApi.updateDevice(deviceData, deviceId);

			await useDevicesStore.getState().getAllDevicesAdmin();

			return response.data;
		} catch (error) {
			console.error('Error al actualizar dispositivo:', error);
			throw error;
		} finally {
			set({ loading: false });
		}
	},
}));

export const getInitialDevice = async () => {
	const userProfile = useUserStore.getState().userProfile;

	try {
		const response = await deviceApi.getAllDevice(userProfile.company.uid);

		if (response && response.data) {
			const parsedDevices = parseDeviceData(response.data);
			useDevicesStore.setState({ allDevices: parsedDevices });

			useDevicesStore.setState((prevState) => ({
				devices: {
					...prevState,
					...response.data[0],
				},
			}));
		}
	} catch (err) {
		console.error(err);
	} finally {
		setLoading(false);
	}
};

const setLoading = (loading: boolean) => {
	useDevicesStore.setState((prevState) => ({
		...prevState,
		loading,
	}));
};

function parseDeviceData(devices: any[]): DeviceData[] {
	return devices.map((device: any) => {

		const parsedDevice: DeviceData = {
			uid: device.uid,
			deviceName: device.deviceName,
			location: device.location,
			blynkId: device.blynkId,
			companyId: device.companyId,
			isConnected: device.isConnected,
			sensors: device.sensors || [],
			createdAt: device.createdAt,
			updatedAt: device.updatedAt,
		};

		if (device.auth_token) {
			parsedDevice.auth_token = device.auth_token;
		}

		return parsedDevice;
	});
}

export default useDevicesStore;
